import { GrantedBy, VestingScheduleData } from "../store/useGrantDetailsStore";
import { ConversionMethodology, PlanExercise } from "./Exercise";
import { PlanVesting } from "./Vest";
import { TriggerType } from "./VestingTemplate";
import { GrantLetterTemplate } from "./SiteSettings";
import { GrantApproval } from "./GrantApproval";

export type Grant = {
  id?: string;
  planid: string;
  planName: string;
  planType: string;
  optionHolderId: string;
  grantIdentifier: string;
  optionHolderName: string;
  optionHolderState: string;
  optionsGranted: number;
  optionsVested: number;
  optionsLapsed: number;
  optionsLapsedSurrendered?: number;
  availableToAccelerate: number;
  grantPrice: number;
  optionsExercised: number;
  optionsExercisedWithCash: number;
  optionsExercisedWithStock: number;
  optionsForfeited: number;
  vestingTemplateName: string;
  grantDate: string;
  employeeId: string;
  employeeName: string;
  employeeIdentifier: string;
  isExerciseRequested: boolean;
  isExerciseAllowed: boolean;
  isVestRequested: boolean;
  vestingId: string;
  employmentStatus: string;
  vestingDate: string;
  vestingDateType: VestingDateType;
  vestingPeriod?: number;
  vestingInterval?: number;
  cliffPeriod: number;
  vestingTriggerType: TriggerType;
  grantLetterLink?: string;
  customizedGrantLetter: boolean;
  grantLetterTemplateId?: string;
  grantLetterTemplateName?: string;
  unVested?: number;
  netVested?: number;
  vestRequestDate?: Date;
  exerciseRequestDate?: Date;
  isFractional: boolean;
  availableStates: string[];
  isStateChangeAllowed: boolean;
  isWithdrawLapsed: boolean;
  isPausedVesting: boolean;
  pausedVestingDate: string;
  actualVestingDay: string;
  grantType?: string;
  liquidityGroupType?: string;
  cumulativeGrantedOptions: number;
  isGrantLetterAvailable?: boolean;
  tags?: { label: string; value: string }[];
  ratio?: number;
  sharePrice?: number;
  pricePerOption?: number;
  conversionMethodology?: ConversionMethodology;
  allocationMatrixId?: string;
  allocationMatrixName?: string;
  matchedRule?: Record<string, any>;
  note?: string;
  isRepriced?: boolean;
  customFieldValues?: Record<string, any>;
  remarks?: string;
  soldOptions?: number;
  grantApprovals?: GrantApproval[];
  grantOfferedAt?: string | Date | null;
  grantOfferExpiresAt?: string | Date | null;
  grantOfferDaysRemaining?: number | null;
  isGrantOfferExpired?: boolean;
};
export interface PlanDetails {
  [planId: string]: {
    name: string;
    type: string;
    isFractional: boolean;
  };
}
export interface EmployeeDetails {
  [employeeId: string]: {
    name: string;
    hrId: string;
    status: string;
  };
}

export type AddGrantReq = {
  id?: string;
  employeeId: string;
  employeeName: string;
  planId: string;
  planName: string;
  planType: string;
  optionsGranted: number;
  grantPrice: number;
  dateOfGrant: string;
  vestingDateType: VestingDateType;
  vestingTemplate: string;
  vestingId: string;
  vestingDate: string;
  grantType?: string;
  grantLetterType?: string | null;
  grantLetter?: string | null;
  grantLetterFileName?: string | null;
  generateGrantLetter?: boolean;
  customizedGrantLetter: boolean;
  actualVestingDay?: string;
  grantId?: string;
  grantedBY?: string;
  manuallyCreatedTemplate?: boolean;
  templateName?: string;
  vestingCliff?: number;
  vestingScheduleData?: VestingScheduleData[];
  sharePrice?: number;
  valuationId?: string;
  modellingId?: string;
  note?: string;
  remarks?: string;
  tags?: { label: string; value: string }[];
  regenerateGrantLetter?: boolean;
  grantLetterTemplate?: GrantLetterTemplate;
  allocationMatrixId?: string;
  matchedRule?: Record<string, any>;
};

export enum VestingDateType {
  GRANT_DATE = "GRANT_DATE",
  EMPLOYEE_JOINING_DATE = "EMPLOYEE_JOINING_DATE",
  CUSTOM_DATE = "CUSTOM_DATE",
}

export interface AddGrantRes {
  optionHolderId: string;
}

export interface ReportDto {
  base64File: string;
  fileType: string;
}

export interface TenCReportDto {
  eventIds?: string[];
  startDate?: Date;
  endDate?: Date;
  employeeIds?: string[];
  maskPiiData?: boolean;
}

export interface OptionStateChangeReq {
  optionHolderIds: string[];
  optionHolderState: string;
  lapseWithdrawed: boolean;
  note?: string;
  mailToBeSent: boolean;
  acceptanceNumber?: number;
  toSubState?: "approved" | "rejected";
  overridePendingSignOff?: boolean;
}
export interface PlanStateChangeDto {
  esopPlanId: string;
  esopPlanState: string;
  planPoolSize: number;
  planStartDate: string;
  approvalDate?: string;
  note?: string;
}

export interface GrantActivity {
  optionHolderId: string;
  optionHolderName: string;
  optionState: string;
  dateOfTransaction: string;
}

export interface EsopCreateDto {
  esopPoolCount: number;
}
export interface TrustDetails {
  id: string;
  trustName: string;
  trustPoolSize: number;
  availableShares: number;
}

export interface Template {
  s3link: string;
}

export interface CustomResponse<T> {
  data: T;
  message: string;
}
export interface UploadTemplate {
  uploadTemplate: string;
}

export interface CustomError<T> {
  data: any;
  errors: T;
}
export interface ValidateRes {
  vestingErrors: Errors[];
  employeeErrors: Errors[];
  grantErrors: Errors[];
  exerciseErrors: Errors[];
  basicValidationErrors: Errors[];
}

export interface Errors {
  id: string;
  name: string;
  errors: string;
}

export interface ImportRes {
  name: string;
  existingNumber: number;
  afterNumber: number;
  addedNumber: number;
}
export interface SignatureAttachment {
  signature: string;
  origin?: string;
  entityIds: number[] | string[];
  note?: string;
  overridePendingSignOff?: boolean;
}

export interface CreatePoolDto {
  poolSize: number;
  poolCreationDate: string;
}

export interface PoolDetailsDto {
  isPoolExists: boolean;
  poolSize: number;
  poolCreationDate: string;
}

export enum PoolTransactionType {
  Created = "Created",
  Amended = "Amended",
}

export interface PoolTransaction {
  id: string;
  poolId: string;
  companyId: string;
  transactionDate: string;
  transactionType: PoolTransactionType;
  noOfOptions: number;
  notes: string | null;
  isDeleted: boolean;
  createdBy: string | null;
  updatedBy: string | null;
}

export interface PoolTransactionCreateDto {
  transactionDate: string;
  transactionType: PoolTransactionType;
  noOfOptions: number;
  notes?: string;
}

export interface PoolTransactionUpdateDto {
  id: string;
  transactionDate?: string;
  transactionType?: PoolTransactionType;
  noOfOptions?: number;
  notes?: string;
}

export interface SettlementSignatureDto {
  signature: string;
  employeeId: string;
}

export interface UpdateSettlementLetterDto {
  fileUrl: string;
  base64file: string;
  employeeId: string;
}
export interface ValidationObj {
  name: string;
  value: any;
  error: any;
  valid: boolean;
  warning?: string;
}

export interface AccelerateVestingDto {
  currentVested: number;
  availableToAccelerate: number;
  grantId: string;
  numberOfOptionsToBeAccelerated: number;
  accelerateDate: string;
  duringResignation?: boolean;
}

export interface OnboardingReportDto {
  file: string;
  dateTillVestingToBeDone: string;
}

export interface GrantValidation {
  hasErrors: boolean;
  fileUploaded: boolean;
  fileValidated: boolean;
  data: ValidationObj[][];
}

export interface GrantResponse {
  status: string;
  validationData: ValidationObj[][];
  message: string;
  hasErrors: boolean;
  uploadId?: string;
}

export interface DownloadConfig {
  fromDate?: Date;
  toDate?: Date;
  datasource?: string;
  hidePiiData?: boolean;
}

export interface PendingRequest {
  id?: string;
  planId?: string;
  subject: string;
  requestDate?: Date;
  exerciseDate?: Date;
  lastDate?: Date;
}

export interface ExerciseRow {
  grantId: string;
  exerciseDate: string;
  cashExercisedOptions: number;
  stockExercisedOptions: number;
}
export interface CumulatedExercise extends ExerciseRow {
  cumulativeExercisedOptions: number;
}
